import React from 'react';
import { motion } from 'framer-motion';
import {
    ArrowRight,
    PlayCircle,
    Quote,
    Droplets,
    Sprout,
    Activity,
    ScrollText,
    Leaf,
    UserCheck,
    Sun,
    FlaskConical,
    Wind,
    Award,
    CheckCircle2,
    Users,
    Mountain
} from 'lucide-react';
import { Link } from 'react-router-dom';
import topview from '../assets/topview.jpeg';
import donat from '../assets/Musical.jpeg';
import medi from '../assets/medi.jpg';
import healing from '../assets/healing.jpg';
import botree from '../assets/botree.jpeg';
import main from '../assets/main.jpeg';
import logo from '../assets/logo.png';
import up from '../assets/up.png';
import down from '../assets/down.png';

export default function Home() {
    // UPDATED COLOR VARIABLES
    const darkGreen = "#0B1A10"; // Richer, more organic deep green
    const goldAccent = "#D4AF37"; // Sampled directly from the bird logo core
    const creamBg = "#FDFBF7";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const reviews = [
        {
            name: "Anura Perera",
            location: "Colombo",
            text: "The holistic approach at Boseth completely changed my perspective on health. The connection to nature is felt in every treatment."
        },
        {
            name: "Sarah Jenkins",
            location: "Wellness Retreat Guest",
            text: "Deeply rooted in tradition but delivered with such care. My chronic stress levels have dropped significantly after the first month."
        },
        {
            name: "Lakmal Gunawardena",
            location: "Kandy",
            text: "A truly authentic experience. You can feel the generational wisdom in their methods. Highly recommended for spiritual healing."
        }
    ];

    // Duplicated EXACTLY once to create a seamless infinite sliding track
    const slidingReviews = [...reviews, ...reviews];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#D4AF37] selection:text-white relative overflow-hidden">

            {/* PAPER TEXTURE OVERLAY */}
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* 1. HERO SECTION */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#020617" }}>
                <div className="absolute inset-0 z-0">
                    <motion.img
                        initial={{ scale: 1.1, opacity: 1 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 3, ease: "easeOut" }}
                        src={main}
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                        alt="Boseth Wellness Aerial View"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#1a365d]/50 via-[#0f172a]/30 to-[#020617]/80 z-20" />
                </div>

                <div className="relative z-20 text-center text-white px-4 max-w-7xl">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="flex flex-col items-center mb-6 md:mb-10"
                    >
                        <div className="mb-6 md:mb-8 p-4 md:p-5 bg-white/10 backdrop-blur-2xl rounded-full border border-white/20 shadow-2xl">
                            <img src={logo} alt="Logo" className="w-12 h-12 md:w-16 md:h-16 object-contain" />
                        </div>
                        <span
                            style={{ color: goldAccent, textShadow: '0px 2px 8px rgba(0,0,0,0.5)' }}
                            className="text-[9px] md:text-[10px] tracking-[0.3em] md:tracking-[0.5em] uppercase font-bold flex items-center justify-center gap-2 md:gap-4 px-2"
                        >
                            <div className="w-8 md:w-12 h-[1px]" style={{ backgroundColor: `${goldAccent}80` }} />
                            Awaken Wellness From The Roots
                            <div className="w-8 md:w-12 h-[1px]" style={{ backgroundColor: `${goldAccent}80` }} />
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 1.2 }}
                        style={{ textShadow: '0px 4px 20px rgba(0,0,0,0.6)' }}
                        className="font-serif text-4xl sm:text-6xl md:text-8xl lg:text-[110px] mb-6 md:mb-8 leading-[1.1] md:leading-[0.9] tracking-tighter"
                    >
                        Welcome To <br/> <span className="font-light" style={{ color: goldAccent }}>Boseth</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                        style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
                        className="text-xs md:text-xl font-medium mb-8 md:mb-12 max-w-4xl mx-auto text-white/90 leading-relaxed uppercase tracking-[0.15em] md:tracking-[0.2em] px-4"
                    >
                        Nurturing health, honoring tradition, and uplifting communities.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.3 }}
                        className="flex flex-col sm:flex-row gap-5 md:gap-8 justify-center items-center"
                    >
                        <Link to="" className="inline-block">
                            <button
                                style={{ backgroundColor: goldAccent }}
                                className="text-white px-10 md:px-12 py-4 md:py-5 rounded-full font-bold uppercase text-[11px] md:text-[12px] tracking-[0.2em] transition-all duration-500 hover:scale-105 shadow-2xl hover:brightness-110 whitespace-nowrap"
                            >
                                Join Our Journey
                            </button>
                        </Link>
                        <button className="flex items-center gap-3 md:gap-5 text-white uppercase text-[11px] md:text-[12px] tracking-[0.2em] font-bold group whitespace-nowrap">
                            <div
                                className="p-3 md:p-4 border border-white/30 rounded-full transition-all duration-500 bg-white/5 backdrop-blur-lg group-hover:border-white"
                                style={{ '--hover-bg': goldAccent }}
                            >
                                <PlayCircle size={20} className="group-hover:text-[#D4AF37] transition-colors" />
                            </div>
                            The Philosophy
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* 2. CENTERED PHILOSOPHY SECTION */}
            <section className="relative py-20 md:py-32 px-4 sm:px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-4 md:mb-6">
                            <img src={up} alt="Flourish Top" className="w-auto h-12 md:h-20 object-contain" />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] md:tracking-[0.4em] text-[10px] md:text-[11px] block mb-4 md:mb-6">
                            Our Core Philosophy
                        </span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#0B1A10] mb-6 md:mb-8 leading-tight">
                            Elevating Your Spirituality
                        </h2>
                        <p className="text-gray-600 text-base sm:text-lg md:text-xl font-light leading-relaxed mb-6 md:mb-10 max-w-3xl mx-auto px-2">
                            As the community in Boseth Wellness Foundation, our philosophy is that we believe that this world is a place designed for a kind of rehabilitation. Therefore we should strive to elevate our spirituality. To elevate our spirituality, our physical, mental and social well-being must be enhanced, and our foundation's services are aimed at enhancing that well-being.
                        </p>
                        <div className="flex justify-center mt-4 md:mt-6">
                            <img src={down} alt="Divider Bottom" className="w-auto h-3 md:h-4 object-contain" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 3. SPLIT IMAGE / HARMONY SECTION */}
            <section className="relative pb-20 md:pb-32 pt-6 md:pt-10 px-4 sm:px-6 overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 md:gap-20 lg:gap-16 items-center">
                        <motion.div {...fadeIn} className="relative flex gap-4 items-stretch h-[350px] sm:h-[450px] md:h-[600px]">
                            <div className="flex flex-col gap-4 w-1/2">
                                <div className="h-1/2 overflow-hidden rounded-2xl shadow-lg border border-black/5">
                                    <img src={donat} className="w-full h-full object-cover" alt="Traditional Practice" />
                                </div>
                                <div className="h-1/2 overflow-hidden rounded-2xl shadow-lg border border-black/5">
                                    <img src={botree} className="w-full h-full object-cover" alt="Natural World" />
                                </div>
                            </div>
                            <div className="w-1/2 overflow-hidden rounded-2xl shadow-xl border border-black/5">
                                <img src={healing} className="w-full h-full object-cover" alt="Harmony" />
                            </div>
                        </motion.div>

                        <motion.div {...fadeIn} className="space-y-6 md:space-y-8">
                            <div className="space-y-4 md:space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-1 border rounded-full" style={{ borderColor: goldAccent }}>
                                        <div className="w-1 h-1 rounded-full" style={{ backgroundColor: goldAccent }} />
                                    </div>
                                    <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] text-[10px]">The Approach</span>
                                </div>
                                <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight" style={{ color: darkGreen }}>
                                    A Profound Sense <br/>
                                    <span className="font-light" style={{ color: goldAccent }}>of Harmony</span>
                                </h2>
                                <p className="text-gray-600 leading-relaxed text-base md:text-lg font-light max-w-xl">
                                    At Boseth Wellness Foundation, we wholeheartedly embrace the philosophy that true well-being is born from a profound sense of harmony—a delicate and dynamic balance encompassing the mind, body, spirit, and the natural world.
                                </p>
                            </div>

                            <div className="space-y-6 md:space-y-8 pt-2">
                                <div className="flex gap-4 md:gap-5 items-start">
                                    <CheckCircle2 size={24} style={{ color: goldAccent }} className="mt-1 shrink-0" strokeWidth={1.5} />
                                    <div className="space-y-1">
                                        <h4 className="font-serif text-xl md:text-2xl" style={{ color: darkGreen }}>Traditional Consciousness</h4>
                                        <p className="text-gray-500 text-sm leading-relaxed max-w-md">Rooted deeply in the rich and timeless traditional rural consciousness of Sri Lanka, preserving healing knowledge passed down through generations.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 md:gap-5 items-start">
                                    <CheckCircle2 size={24} style={{ color: goldAccent }} className="mt-1 shrink-0" strokeWidth={1.5} />
                                    <div className="space-y-1">
                                        <h4 className="font-serif text-xl md:text-2xl" style={{ color: darkGreen }}>Sustainable Living</h4>
                                        <p className="text-gray-500 text-sm leading-relaxed max-w-md">We strive to share this profound wisdom with the global community, inspiring a stronger connection to holistic health and the nurturing power of nature.</p>
                                    </div>
                                </div>
                            </div>

                            <Link to="" className="inline-flex items-center gap-4 md:gap-6 group pt-4">
                                <span className="font-bold uppercase text-[11px] md:text-[12px] tracking-[0.2em] border-b border-transparent group-hover:border-[#D4AF37] transition-all">Explore our dedication</span>
                                <div
                                    className="w-10 h-10 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition-all group-hover:text-white"
                                    style={{ borderColor: `${darkGreen}30` }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkGreen}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <ArrowRight size={18} />
                                </div>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 4. MISSION STATEMENT SECTION */}
            <section className="py-20 md:py-32 bg-white relative px-4 sm:px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <ScrollText style={{ color: goldAccent }} size={28} className="mx-auto mb-4 md:mb-6 opacity-60" />
                    <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] md:tracking-[0.5em] text-[10px] md:text-[11px] block mb-3">Our Sacred Mission</span>
                    <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl tracking-tight mb-12 md:mb-24" style={{ color: darkGreen }}>Sharing Profound Wisdom</h2>

                    <div className="grid md:grid-cols-3 gap-8 md:gap-10">
                        {[
                            { step: "PRESERVE", title: "Healing Knowledge", desc: "Protecting the priceless wisdom passed down through generations of Sri Lankan heritage.", icon: Activity },
                            { step: "INSPIRE", title: "Global Connection", desc: "Striving to share holistic health and sustainable living with the global community.", icon: Droplets },
                            { step: "NURTURE", title: "Power of Nature", desc: "Connecting the soul to the nurturing power of the natural world that surrounds us.", icon: Sprout }
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                {...fadeIn}
                                transition={{ delay: i * 0.1 }}
                                className="group p-8 sm:p-14 bg-[#FDFBF7] rounded-t-[5rem] sm:rounded-t-[7rem] rounded-b-[2rem] border border-[#E5E0D8]/60 text-center transition-all duration-700 shadow-sm hover:shadow-2xl"
                                style={{ '--hover-border': goldAccent }}
                            >
                                <div className="text-[10px] font-bold tracking-[0.4em] mb-6 md:mb-10 opacity-70" style={{ color: goldAccent }}>{item.step}</div>
                                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 md:mb-10 rounded-full bg-white flex items-center justify-center border border-[#E5E0D8] group-hover:border-[#D4AF37]/30 transition-all duration-500">
                                    <item.icon style={{ color: goldAccent }} size={24} strokeWidth={1.5} />
                                </div>
                                <h4 className="font-serif text-2xl md:text-3xl mb-4 md:mb-6" style={{ color: darkGreen }}>{item.title}</h4>
                                <p className="text-gray-500 font-light leading-relaxed text-[14px] md:text-[15px] px-2">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 5. WHY CHOOSE US */}
            <section className="relative py-20 md:py-32 overflow-hidden px-4 sm:px-6" style={{ backgroundColor: darkGreen }}>
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1600334129128-585c6edee0ad?q=80&w=2000"
                        className="w-full h-full object-cover opacity-10"
                        alt="Wellness Background"
                    />
                </div>

                <div className="relative z-10 max-w-6xl mx-auto text-center">
                    <motion.div {...fadeIn} className="mb-12 md:mb-20">
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] text-[10px] block mb-3">The Boseth Way</span>
                        <h2 className="font-serif text-3xl sm:text-5xl text-white tracking-tight">
                            Rooted in Tradition, <br/>
                            <span className="font-light" style={{ color: goldAccent }}>Focused on Tomorrow</span>
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-12 md:gap-y-16 gap-x-8 md:gap-x-12">
                        {[
                            { title: "Traditional Consciousness", desc: "Experience the timeless rural wisdom of Sri Lankan healing.", icon: Leaf },
                            { title: "Sustainable Living", desc: "Embracing lifestyles that honor the natural world and its resources.", icon: Sun },
                            { title: "Holistic Health", desc: "A delicate balance encompassing the mind, body, and spirit.", icon: UserCheck },
                            { title: "Generational Wisdom", desc: "Knowledge cherished and passed down through innumerable generations.", icon: FlaskConical },
                            { title: "Dynamic Balance", desc: "Restoring the harmony between humans and the environment.", icon: Wind },
                            { title: "Global Community", desc: "Bringing local healing traditions to inspire the entire world.", icon: Award }
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                {...fadeIn}
                                transition={{ delay: i * 0.1 }}
                                className={`flex flex-col items-center space-y-4 md:space-y-6 relative ${i % 3 !== 2 ? 'md:border-r border-white/10' : ''} ${i < 3 ? 'md:pb-12 md:border-b border-white/10' : 'md:pt-4'}`}
                            >
                                <div className="p-3 md:p-4 bg-white/5 backdrop-blur-md rounded-full border border-white/10 group hover:bg-[#D4AF37] transition-all duration-500">
                                    <item.icon color="white" size={24} strokeWidth={1.5} />
                                </div>
                                <div className="space-y-2 px-2">
                                    <h4 className="text-white font-serif text-lg md:text-xl tracking-wide">{item.title}</h4>
                                    <p className="text-white/40 text-xs md:text-sm leading-relaxed max-w-[280px] mx-auto font-light">
                                        {item.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 6. REVIEWS CONTAINER SLIDER (STARS REMOVED) */}
            <section className="py-20 md:py-32 bg-[#FDFBF7] relative overflow-hidden px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div {...fadeIn} className="flex flex-col items-center text-center mb-10 md:mb-16">
                        <div className="flex items-center gap-3 mb-6 md:mb-8">
                            <div className="w-4 h-4 border rounded-full flex items-center justify-center" style={{ borderColor: goldAccent }}>
                                <div className="w-1 h-1 rounded-full" style={{ backgroundColor: goldAccent }} />
                            </div>
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] md:tracking-[0.5em] text-[10px]">
                                Patient Experiences
                            </span>
                        </div>

                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight max-w-4xl" style={{ color: darkGreen }}>
                            Stories of <br className="hidden md:block" /> Healing
                            <span className="font-light" style={{ color: goldAccent }}> & Transformation</span>
                        </h2>
                    </motion.div>

                    {/* Responsive Sliding Track Container */}
                    <div className="w-full overflow-hidden relative pb-6 md:pb-10">
                        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#FDFBF7] to-transparent z-10 hidden md:block" />
                        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#FDFBF7] to-transparent z-10 hidden md:block" />

                        <motion.div
                            className="flex w-max"
                            animate={{ x: [0, "-50%"] }}
                            transition={{ ease: "linear", duration: 20, repeat: Infinity }}
                        >
                            {slidingReviews.map((rev, i) => (
                                <div
                                    key={i}
                                    className="w-[85vw] sm:w-[420px] md:w-[400px] px-3 md:px-4 flex flex-col shrink-0"
                                >
                                    <div className="bg-white p-6 md:p-10 h-full rounded-[2.5rem] border border-[#E5E0D8]/50 relative hover:shadow-2xl transition-all duration-500 group flex flex-col justify-between">
                                        <div>
                                            <Quote className="absolute top-6 right-6 md:top-10 md:right-10 opacity-[0.05]" style={{ color: goldAccent }} size={60} />
                                            <p className="text-gray-600 mb-6 md:mb-10 leading-relaxed font-light text-base md:text-lg pt-4">"{rev.text}"</p>
                                        </div>
                                        <div className="pt-6 border-t border-gray-50 mt-auto">
                                            <h4 className="font-serif text-lg md:text-xl mb-1" style={{ color: darkGreen }}>{rev.name}</h4>
                                            <p className="text-[10px] tracking-[0.3em] uppercase font-bold opacity-80" style={{ color: goldAccent }}>{rev.location}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 7. COMMUNITY IMPACT SECTION */}
            <section className="relative py-20 md:py-32 bg-[#FDFBF7] overflow-hidden px-4 sm:px-6">
                <div className="relative z-20 max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
                        <motion.div {...fadeIn}>
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] md:tracking-[0.5em] text-[10px] md:text-[11px] block mb-3">Community Impact</span>
                            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl mb-6 md:mb-8 leading-tight" style={{ color: darkGreen }}>
                                Become a Guardian <br/>
                                <span className="font-light" style={{ color: goldAccent }}>of Tradition</span>
                            </h2>
                            <p className="text-gray-600 text-base md:text-lg font-light leading-relaxed mb-8 md:mb-10 max-w-xl">
                                We invite passionate nature lovers, students, and practitioners to join our volunteer network and create a lasting Community Impact. Through Rural Outreach and Eco-Conservation, you play a vital role in preserving ancestral wisdom and uplifting our local environments.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-12">
                                <div className="flex items-center gap-4 p-4 md:p-5 bg-white rounded-2xl border border-[#E5E0D8] hover:border-[#D4AF37] transition-all shadow-sm">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${goldAccent}15` }}>
                                        <Sprout size={18} style={{ color: goldAccent }} />
                                    </div>
                                    <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: darkGreen }}>Rural Outreach</span>
                                </div>
                                <div className="flex items-center gap-4 p-4 md:p-5 bg-white rounded-2xl border border-[#E5E0D8] hover:border-[#D4AF37] transition-all shadow-sm">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${goldAccent}15` }}>
                                        <Mountain size={18} style={{ color: goldAccent }} />
                                    </div>
                                    <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: darkGreen }}>Eco-Conservation</span>
                                </div>
                            </div>

                            <div className="flex justify-start">
                                <button style={{ backgroundColor: goldAccent }} className="text-white px-10 md:px-12 py-4 md:py-5 rounded-full font-bold uppercase text-[11px] tracking-[0.2em] transition-all duration-500 hover:scale-105 shadow-xl flex items-center gap-4 group whitespace-nowrap">
                                    <Users size={18} className="group-hover:animate-pulse" /> Join as a Volunteer
                                </button>
                            </div>
                        </motion.div>

                        <motion.div {...fadeIn} className="relative">
                            <div
                                className="aspect-square bg-[#1a365d]/5 backdrop-blur-3xl p-4 md:p-6 rounded-[2.5rem] sm:rounded-[5rem] border border-white/40 shadow-[0_0_80px_rgba(26,54,93,0.1)] relative overflow-hidden group transition-all duration-1000"
                                style={{
                                    maskImage: 'radial-gradient(circle, black 70%, transparent 100%)',
                                    WebkitMaskImage: 'radial-gradient(circle, black 70%, transparent 100%)'
                                }}
                            >
                                <img
                                    src={medi}
                                    className="w-full h-full object-cover rounded-[2rem] sm:rounded-[4rem] grayscale group-hover:grayscale-0 transition-all duration-1000 opacity-80 group-hover:opacity-100"
                                    alt="Volunteer Work"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 8. FINAL JOURNEY SECTION */}
            <section className="relative py-24 md:py-40 text-white overflow-hidden px-4 sm:px-6">
                <div
                    className="absolute inset-0 z-0 bg-fixed bg-center bg-cover"
                    style={{ backgroundImage: `url(${topview})` }}
                >
                    <div className="absolute inset-0 z-10" style={{ backgroundColor: `${darkGreen}CC` }} />
                </div>

                <div className="relative z-20 max-w-7xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] text-[10px] block mb-4 md:mb-6">The Next Step</span>
                        <h2 className="font-serif text-4xl sm:text-6xl md:text-8xl mb-6 md:mb-8 text-white tracking-tight">Start Your Journey</h2>
                        <p className="text-white/60 font-light text-base md:text-lg mb-8 md:mb-12 uppercase tracking-[0.15em] md:tracking-[0.2em]">Start your journey with us today.</p>
                        <div className="flex justify-center">
                            <button
                                style={{ backgroundColor: goldAccent }}
                                className="text-white px-12 md:px-16 py-4 md:py-6 rounded-full font-bold uppercase text-[11px] md:text-[12px] tracking-[0.2em] md:tracking-[0.3em] hover:bg-white hover:text-[#0B1A10] transition-all duration-500 shadow-2xl whitespace-nowrap"
                            >
                                Contact Us Today
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}